import type {
	SummarizableResourceType,
	SummarizationParams,
} from '@sharefiledev/qna-pilet-sdk';
import { PiletApi } from '@sharefiledev/sharefile-appshell';
import { getApiBaseUrl } from 'data/helpers';
import { pollWithConstantBackOff } from 'utils/poller';
import { ContentQnAClient, initializeClient } from './client';
import {
	FeedbackType,
	ModelInvocationStatus,
	StatusRecord,
	SummarizationJobStatus,
	Summary,
	SummaryResponse,
} from './contentqnaPluginTypes';
import {
	GenerationDeclinedByLLMError,
	GenerationFailedError,
	GenerationRateLimitError,
	InaccessibleResourceError,
	SummaryTaskInitiationError,
} from './errors';
import { AiTaskInitiationError } from './errors/AiTaskInitiationError';
import { SummaryFetchFailedError } from './errors/SummaryFetchFailedError';
import { SummaryTaskAlreadyCompletedError } from './errors/SummaryTaskAlreadyCompletedError';
import { QnAContextMessage, QnAContextMessageStatus, QnACtx } from './qnactxPluginTypes';

const llmDeclinedStatuses = [
	ModelInvocationStatus.DeclinedHarmful,
	ModelInvocationStatus.DeclinedInputTooLong,
	ModelInvocationStatus.DeclinedMisuse,
	ModelInvocationStatus.DeclinedUnknown,
];

export class ContentQnASdk {
	private initializedClient: ContentQnAClient | undefined;

	public Initialize(client: ContentQnAClient) {
		this.initializedClient = client;
	}

	/**
	 *
	 * @param params
	 * @returns
	 * @throws @see {SummaryTaskInitiationError} if starting the generation task fails
	 * @throws @see {GenerationRateLimitError} if the generation task is rate limited (server normal operation, but rate limit is exceeded)
	 * @throws @see {GenerationFailedError} if the generation task fails (server normal operation, but generation fails, or fetching the generated summary fails)
	 * @throws @see {GenerationDeclinedByLLMError} if the generation task is declined (server normal operation, but generation is declined by the AI due to misuse, harmful propmt, or an uncategorizable but "shady" input it doesn't wish to process)
	 * @throws @see {SummaryFetchFailedError} if fetching the generated summary fails
	 * @throws @see {InaccessibleResourceError} if the resource is inaccessible for the user
	 */
	public async generateSummary(
		params: SummarizationParams<SummarizableResourceType>,
		isRetry: boolean,
		ac: AbortController = new AbortController()
	): Promise<Summary> {
		try {
			const {
				status: { taskId },
			} = await this.startSummarization(params, isRetry, ac);

			const result = await pollWithConstantBackOff<StatusRecord>({
				pollFn: async () => {
					return await this.initializedClient!.getSummaryTaskStatus(taskId, ac);
				},
				validateFn: (statusRecord: StatusRecord) => {
					if (
						statusRecord.status === SummarizationJobStatus.Failed ||
						statusRecord.status === SummarizationJobStatus.Unknown
					) {
						if (typeof statusRecord?.result === 'string') {
							if (llmDeclinedStatuses.includes(statusRecord.result)) {
								throw new GenerationDeclinedByLLMError(statusRecord.result);
							}
						}
						throw new GenerationFailedError(
							undefined,
							'Generation failed: when polling and checking for status, the status record "result" field was a non-string value, expected a string representing a ModelInvocationStatus'
						);
					}
					return statusRecord.status === SummarizationJobStatus.Completed;
				},
				delayInMilliSeconds: 1500,
				maxAttempts: 120,
			});

			if (!result.summaryRID) {
				throw new GenerationFailedError(
					undefined,
					'Generation failed: retrieved task record status shows "' +
						SummarizationJobStatus.Completed +
						'" but did not include a resource ID for the generated summary'
				);
			}

			return await this.getSummaryByRID(result.summaryRID, ac);
		} catch (error) {
			if (error instanceof SummaryTaskInitiationError && error.isRateLimitError()) {
				throw new GenerationRateLimitError(error);
			}
			if (
				error instanceof SummaryTaskInitiationError &&
				error.isAlreadyCompletedError()
			) {
				return error.getExistingSummary().summary!;
			}

			if (
				error instanceof SummaryTaskInitiationError &&
				error.isInaccessibleResourceError()
			) {
				throw new InaccessibleResourceError(error);
			}

			if (
				error instanceof GenerationDeclinedByLLMError ||
				error instanceof GenerationFailedError
			) {
				throw error;
			}

			throw new GenerationFailedError(
				error,
				'Generation failed due to an unexpected error'
			);
		}
	}

	/**
	 *
	 * @param summaryRID Summary Resource ID
	 * @param type Feedback type
	 * @param ac
	 */
	public async reportSummaryFeedback(
		summaryRID: string,
		type: FeedbackType,
		ac: AbortController = new AbortController()
	): Promise<void> {
		try {
			await this.initializedClient!.reportSummaryFeedback(summaryRID, type, ac);
		} catch (error) {
			throw new Error('Failed to report summary feedback');
		}
	}

	/**
	 * Starts a summary generation task, and ensures that the response contains a taskId (throws otherwise)
	 * @param params
	 * @param ac
	 * @returns
	 * @throws @see {SummaryTaskInitiationError} if starting the generation task fails
	 */
	private async startSummarization(
		params: SummarizationParams<SummarizableResourceType>,
		isRetry: boolean,
		ac: AbortController = new AbortController()
	): Promise<
		SummaryResponse & {
			status: StatusRecord & { taskId: string };
		}
	> {
		try {
			const task = await this.initializedClient!.createSummary(params, isRetry, ac);
			if (!!task.completed) {
				throw new SummaryTaskAlreadyCompletedError(task);
			}
			if (!task.status) {
				throw new Error('SummaryResponse::status field is empty');
			}

			if (!task.status.taskId || typeof task.status.taskId !== 'string') {
				throw new Error('SummaryResponse::status::taskId field is empty or not a string');
			}

			return { ...task, status: { ...task.status, taskId: task.status.taskId } };
		} catch (error) {
			throw new SummaryTaskInitiationError(error);
		}
	}

	/**
	 * Fetches a summary by its resource ID
	 * @param rid
	 * @param ac
	 * @returns
	 * @throws @see {SummaryFetchFailedError} if fetching the summary fails
	 */
	private async getSummaryByRID(
		rid: string,
		ac: AbortController = new AbortController()
	): Promise<Summary> {
		try {
			return await this.initializedClient!.getSummaryByRID(rid, ac);
		} catch (error) {
			throw new SummaryFetchFailedError(error);
		}
	}

	/**
	 *
	 * @param sourceResourceRid
	 * @param request
	 * @param ac
	 * @returns
	 */
	public async generateQnAResponse(
		sourceResourceRid: string,
		request: string,
		retry?: boolean,
		ac: AbortController = new AbortController()
	): Promise<QnAContextMessage> {
		try {
			const qnaCtxMsg = await this.initializedClient!.createQnARequest(
				sourceResourceRid,
				request,
				retry ?? false,
				ac
			);
			return qnaCtxMsg;
		} catch (error) {
			const aiError = new AiTaskInitiationError(error);
			if (aiError.isRateLimitError()) {
				throw new GenerationRateLimitError(aiError);
			} else if (aiError.isInaccessibleResourceError()) {
				throw new InaccessibleResourceError(aiError);
			}

			throw new GenerationFailedError(
				error,
				'Generation failed due to an unexpected error'
			);
		}
	}

	/**
	 *
	 * @param taskId
	 * @param contextRID
	 * @param ac
	 * @returns
	 */
	public async pollForQnAResponse(
		taskId: string,
		contextRID?: string,
		ac: AbortController = new AbortController()
	): Promise<QnAContextMessage> {
		try {
			// Poll for the status of the QnA task
			const result = await pollWithConstantBackOff<QnAContextMessage>({
				pollFn: async () => {
					return await this.initializedClient!.getQnARequestStatus(
						taskId,
						contextRID ?? '',
						ac
					);
				},
				validateFn: (qnaContextMessage: QnAContextMessage) => {
					if (qnaContextMessage.status === QnAContextMessageStatus.Failed) {
						if (typeof qnaContextMessage.result === 'string') {
							if (llmDeclinedStatuses.includes(qnaContextMessage.result)) {
								throw new GenerationDeclinedByLLMError(qnaContextMessage.result);
							}
						}

						throw new GenerationFailedError(
							undefined,
							'QnA failed: when polling and checking for status, the QnA request has been marked as Failed'
						);
					}
					return qnaContextMessage.status === QnAContextMessageStatus.Completed;
				},
				delayInMilliSeconds: 1500,
				maxAttempts: 120,
			});

			if (
				result.response === null ||
				result.response === undefined ||
				result.response.statements.length === 0 ||
				result.response.statements[0].sentence === ''
			) {
				throw new GenerationFailedError(
					undefined,
					'QnA failed: returned response is an invalid response'
				);
			}

			return result;
		} catch (error) {
			if (
				error instanceof GenerationDeclinedByLLMError ||
				error instanceof GenerationFailedError
			) {
				throw error;
			}

			throw new GenerationFailedError(
				error,
				'Generation failed due to an unexpected error'
			);
		}
	}

	public async getQnAContextForResource(
		sourceResourceId: string,
		ac: AbortController = new AbortController()
	): Promise<QnACtx> {
		try {
			return await this.initializedClient!.getQnaContextForResource(sourceResourceId, ac);
		} catch (error) {
			throw new Error(`Failed to get QnA context for source RID: ${sourceResourceId}`);
		}
	}

	public async deleteQnAContext(
		contextRid: string,
		ac: AbortController = new AbortController()
	): Promise<void> {
		try {
			await this.initializedClient!.deleteQnAContext(contextRid, ac);
		} catch (error) {
			throw new Error(`Failed to delete QnA context for context RID: ${contextRid}`);
		}
	}

	/**
	 *
	 * @param qnaContextRID QnA Context Resource ID
	 * @param type Feedback type
	 * @param taskId Task ID
	 * @param ac
	 */
	public async reportQnAFeedback(
		qnaContextRID: string,
		type: FeedbackType,
		taskId?: string,
		ac: AbortController = new AbortController()
	): Promise<void> {
		try {
			await this.initializedClient!.reportQnAFeedback(qnaContextRID, type, ac, taskId);
		} catch (error) {
			throw new Error('Failed to report QnA feedback');
		}
	}
}

export const contentQnASdk = new ContentQnASdk();

export function configureContentQnASdk(app: PiletApi) {
	contentQnASdk.Initialize(
		initializeClient({
			baseUrl: getApiBaseUrl(app, 'contentqna'),
		})
	);
}
